/*
 * Visitor tracking for the interactive globe.
 *
 * Fires once per page load when the visitor grants location permission.
 * Sends a rich embed to a Discord webhook AND appends a row to a Google
 * Sheet via a Google Apps Script Web App URL.
 *
 * ⚠️ Both webhook URLs are baked into the public JS bundle at build time.
 *     They are effectively public — treat them as low-secret, easy to rotate.
 *
 * ─── Google Sheets setup ───────────────────────────────────────────────
 * 1. Create a Google Sheet (any tab name is fine).
 * 2. Extensions → Apps Script. Replace the default with:
 *
 *    function doPost(e) {
 *      const sheet = SpreadsheetApp.getActiveSpreadsheet().getActiveSheet();
 *      if (sheet.getLastRow() === 0) {
 *        sheet.appendRow([
 *          'Timestamp','IPv4','IPv6','IP',
 *          'Browser Lat','Browser Lng','Accuracy (m)','Maps URL',
 *          'Precise City','Precise Region','Precise Country','Precise Postcode',
 *          'IP Country','IP Region','IP City','IP Lat','IP Lng',
 *          'ISP','Org','ASN','Timezone','Device','OS','Browser',
 *          'Is Bot','Is VPN','VPN Reason','Language','Screen','Referrer','User Agent','URL'
 *        ]);
 *      }
 *      const d = JSON.parse(e.postData.contents);
 *      sheet.appendRow([
 *        d.timestamp, d.ipv4, d.ipv6, d.ip,
 *        d.latitude, d.longitude, d.accuracy, d.mapsUrl,
 *        d.preciseCity, d.preciseRegion, d.preciseCountry, d.precisePostcode,
 *        d.ipCountry, d.ipRegion, d.ipCity, d.ipLatitude, d.ipLongitude,
 *        d.isp, d.org, d.asn, d.timezone, d.deviceType, d.os, d.browser,
 *        d.isBot, d.isVpn, d.vpnReason, d.language, d.screen, d.referrer,
 *        d.userAgent, d.url
 *      ]);
 *      return ContentService.createTextOutput(JSON.stringify({ ok: true }))
 *        .setMimeType(ContentService.MimeType.JSON);
 *    }
 *
 * 3. Deploy → New deployment → Type: Web app.
 *    Execute as: Me.  Who has access: Anyone.
 * 4. Copy the deployment URL into REACT_APP_SHEETS_WEBHOOK_URL.
 *
 * ─── Discord setup ─────────────────────────────────────────────────────
 * 1. In your Discord server → Channel settings → Integrations → Webhooks.
 * 2. New Webhook → copy URL → paste into REACT_APP_DISCORD_WEBHOOK_URL.
 */

const DISCORD_URL = process.env.REACT_APP_DISCORD_WEBHOOK_URL || '';
const SHEETS_URL = process.env.REACT_APP_SHEETS_WEBHOOK_URL || '';

// ASN / org substrings that suggest datacenter, hosting or commercial VPN.
// Not exhaustive — heuristic only. False positives are possible on mobile
// carriers that share ASN with hosting arms.
const VPN_HOSTING_PATTERNS = [
    /vpn/i, /proxy/i, /tor exit/i, /anonymizer/i,
    /amazon|aws|\bec2\b/i, /google cloud|gcp/i, /azure|microsoft/i,
    /digitalocean/i, /linode/i, /vultr/i, /\bovh\b/i, /hetzner/i,
    /oracle cloud/i, /alibaba/i, /tencent cloud/i,
    /nordvpn/i, /expressvpn/i, /mullvad/i, /protonvpn/i,
    /surfshark/i, /cyberghost/i, /private internet access/i,
    /leaseweb/i, /choopa/i, /hostwinds/i, /contabo/i, /scaleway/i,
    /m247/i, /datacamp/i, /ipxo/i,
];

const BOT_UA_PATTERNS = [
    /bot\b/i, /crawler/i, /spider/i, /slurp/i, /headlesschrome/i,
    /puppeteer/i, /playwright/i, /selenium/i, /phantomjs/i,
    /curl\//i, /wget\//i, /python-requests/i, /axios\//i, /okhttp/i,
    /go-http-client/i,
];

function parseUserAgent(ua) {
    const isTablet = /iPad|Tablet|(android(?!.*mobile))/i.test(ua);
    const isMobile = !isTablet && /Mobi|Android|iPhone|iPod|Opera Mini|IEMobile/i.test(ua);
    const deviceType = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

    let os = 'Unknown';
    if (/Windows NT 10\.0/.test(ua)) os = 'Windows 10/11';
    else if (/Windows NT 6\.3/.test(ua)) os = 'Windows 8.1';
    else if (/Windows NT 6\.2/.test(ua)) os = 'Windows 8';
    else if (/Windows NT 6\.1/.test(ua)) os = 'Windows 7';
    else if (/Windows/.test(ua)) os = 'Windows';
    else if (/iPhone OS ([\d_]+)|iPad; CPU OS ([\d_]+)/.test(ua)) {
        const m = ua.match(/OS ([\d_]+)/);
        os = 'iOS ' + (m ? m[1].replace(/_/g, '.') : '');
    }
    else if (/Mac OS X ([\d_]+)/.test(ua)) {
        const m = ua.match(/Mac OS X ([\d_]+)/);
        os = 'macOS ' + (m ? m[1].replace(/_/g, '.') : '');
    }
    else if (/Android ([\d.]+)/.test(ua)) {
        const m = ua.match(/Android ([\d.]+)/);
        os = 'Android ' + (m ? m[1] : '');
    }
    else if (/CrOS/.test(ua)) os = 'ChromeOS';
    else if (/Linux/.test(ua)) os = 'Linux';

    let browser = 'Unknown';
    const grab = (re) => { const m = ua.match(re); return m ? m[1] : ''; };
    if (/Edg\/([\d.]+)/.test(ua)) browser = 'Edge ' + grab(/Edg\/([\d.]+)/);
    else if (/OPR\/([\d.]+)/.test(ua)) browser = 'Opera ' + grab(/OPR\/([\d.]+)/);
    else if (/Firefox\/([\d.]+)/.test(ua)) browser = 'Firefox ' + grab(/Firefox\/([\d.]+)/);
    else if (/CriOS\/([\d.]+)/.test(ua)) browser = 'Chrome (iOS) ' + grab(/CriOS\/([\d.]+)/);
    else if (/FxiOS\/([\d.]+)/.test(ua)) browser = 'Firefox (iOS) ' + grab(/FxiOS\/([\d.]+)/);
    else if (/Chrome\/([\d.]+)/.test(ua)) browser = 'Chrome ' + grab(/Chrome\/([\d.]+)/);
    else if (/Version\/([\d.]+).*Safari/.test(ua)) browser = 'Safari ' + grab(/Version\/([\d.]+).*Safari/);

    return { os, browser, deviceType };
}

function detectBot(ua) {
    if (typeof navigator !== 'undefined' && navigator.webdriver === true) return true;
    return BOT_UA_PATTERNS.some((re) => re.test(ua));
}

function detectVpn(org, isp, asn) {
    const haystack = `${org} ${isp} ${asn}`;
    for (const re of VPN_HOSTING_PATTERNS) {
        if (re.test(haystack)) {
            const m = haystack.match(re);
            return { isVpn: true, vpnReason: `matched "${m ? m[0] : ''}"` };
        }
    }
    return { isVpn: false, vpnReason: '' };
}

async function fetchIpInfo(specificIp) {
    const url = specificIp ? `https://ipwho.is/${specificIp}` : 'https://ipwho.is/';
    try {
        const res = await fetch(url);
        if (!res.ok) return null;
        const data = await res.json();
        if (data.success === false) return null;
        return data;
    } catch (err) {
        console.warn('[visitorTracking] IP lookup failed', err);
        return null;
    }
}

// ipify has version-locked endpoints — api4.* only resolves over IPv4,
// api6.* only over IPv6. On dual-stack networks both return; on IPv4-only
// networks the v6 fetch fails silently and we just omit that column.
async function fetchIpAt(host) {
    try {
        const res = await fetch(`https://${host}?format=json`);
        if (!res.ok) return '';
        const data = await res.json();
        return data.ip || '';
    } catch {
        return '';
    }
}

// Reverse geocode the browser's precise GPS fix into a city/region/country.
// bigdatacloud's reverse-geocode-client endpoint is designed for browser
// use (no key, CORS-open) and is much closer to the truth than IP geo,
// because it starts from the actual coordinates the visitor granted.
async function reverseGeocode(lat, lng) {
    if (lat == null || lng == null) return null;
    try {
        const url = `https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${lat}&longitude=${lng}&localityLanguage=en`;
        const res = await fetch(url);
        if (!res.ok) return null;
        const data = await res.json();
        return {
            city: data.city || data.locality || '',
            region: data.principalSubdivision || '',
            country: data.countryName || '',
            countryCode: (data.countryCode || '').toUpperCase(),
            postcode: data.postcode || '',
        };
    } catch (err) {
        console.warn('[visitorTracking] reverse geocode failed', err);
        return null;
    }
}

function googleMapsUrl(lat, lng) {
    if (lat == null || lng == null) return '';
    // The maps.google.com/?q=lat,lng form opens with a dropped pin at the
    // exact coordinates on both desktop and the mobile apps (iOS/Android
    // deep-link handlers pick it up automatically).
    return `https://www.google.com/maps?q=${lat},${lng}`;
}

async function collectVisitorInfo(coords, accuracy) {
    const ua = (typeof navigator !== 'undefined' && navigator.userAgent) || '';
    // Grab both IP versions AND reverse-geocode the browser coords in parallel.
    // Prefer v4 for the IP-geo lookup so region data reflects the address
    // most people care about.
    const [ipv4, ipv6, precise] = await Promise.all([
        fetchIpAt('api4.ipify.org'),
        fetchIpAt('api6.ipify.org'),
        coords ? reverseGeocode(coords[0], coords[1]) : Promise.resolve(null),
    ]);
    const ip = await fetchIpInfo(ipv4);
    const { os, browser, deviceType } = parseUserAgent(ua);
    const isBot = detectBot(ua);

    const org = ip?.connection?.org || '';
    const isp = ip?.connection?.isp || '';
    const asn = ip?.connection?.asn ? `AS${ip.connection.asn}` : '';
    const { isVpn, vpnReason } = detectVpn(org, isp, asn);

    const browserTz = (() => {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; }
        catch { return ''; }
    })();

    return {
        timestamp: new Date().toISOString(),
        ip: ip?.ip || ipv4 || ipv6 || '',
        ipv4,
        ipv6,
        latitude: coords ? coords[0] : null,
        longitude: coords ? coords[1] : null,
        accuracy: accuracy != null ? Math.round(accuracy) : null,
        mapsUrl: coords ? googleMapsUrl(coords[0], coords[1]) : '',
        // Precise = reverse-geocoded from the browser's GPS fix. Meter-level.
        preciseCity: precise?.city || '',
        preciseRegion: precise?.region || '',
        preciseCountry: precise?.country || '',
        preciseCountryCode: precise?.countryCode || '',
        precisePostcode: precise?.postcode || '',
        // IP-based = approximation from the visitor's IP address. City-level
        // at best, sometimes just country-level. Kept as a fallback when
        // location permission was denied, and as a cross-check.
        ipCity: ip?.city || '',
        ipRegion: ip?.region || '',
        ipCountry: ip?.country || '',
        ipCountryCode: ip?.country_code || '',
        ipLatitude: ip?.latitude ?? null,
        ipLongitude: ip?.longitude ?? null,
        isp,
        org,
        asn,
        timezone: ip?.timezone?.id || browserTz,
        deviceType,
        os,
        browser,
        isBot,
        isVpn,
        vpnReason,
        language: (typeof navigator !== 'undefined' && navigator.language) || '',
        screen: typeof window !== 'undefined' ? `${window.screen.width}x${window.screen.height}` : '',
        referrer: (typeof document !== 'undefined' && document.referrer) || 'direct',
        userAgent: ua,
        url: typeof window !== 'undefined' ? window.location.href : '',
    };
}

function countryCodeToFlag(cc) {
    if (!cc || cc.length !== 2) return '';
    return String.fromCodePoint(
        ...[...cc.toUpperCase()].map((c) => 0x1F1A5 + c.charCodeAt(0))
    );
}

async function sendToDiscord(info) {
    if (!DISCORD_URL) return;
    const preciseFlag = countryCodeToFlag(info.preciseCountryCode);
    const ipFlag = countryCodeToFlag(info.ipCountryCode);
    const color = info.isBot ? 0xE74C3C : info.isVpn ? 0xF1C40F : 0x3E38FF;

    const preciseLocParts = [info.preciseCity, info.preciseRegion, info.preciseCountry].filter(Boolean);
    const preciseLoc = preciseLocParts.length
        ? `${preciseFlag} ${preciseLocParts.join(', ')}`.trim()
        : '';
    const ipLoc = [info.ipCity, info.ipRegion, info.ipCountry].filter(Boolean).join(', ') || '—';

    // Discord renders [text](url) as a clickable hyperlink inside embed fields.
    const coordsLine = info.latitude != null
        ? `[\`${info.latitude.toFixed(6)}, ${info.longitude.toFixed(6)}\`](${info.mapsUrl})` +
          (info.accuracy != null ? ` · ±${info.accuracy}m` : '')
        : '—';

    const fields = [
        { name: '📍 Precise coordinates', value: coordsLine, inline: false },
    ];
    // Show the precise location when reverse geocoding worked. Only fall
    // back to the IP-derived location when the precise one isn't available
    // (permission denied, reverse-geocode rate-limited, or offline).
    if (preciseLoc) {
        fields.push({ name: 'Precise location', value: preciseLoc, inline: true });
    } else {
        fields.push({ name: 'IP location (approx.)', value: `${ipFlag} ${ipLoc}`.trim(), inline: true });
    }
    fields.push(
        { name: 'IPv4', value: info.ipv4 || '—', inline: true },
        { name: 'IPv6', value: info.ipv6 ? `\`${info.ipv6}\`` : '—', inline: true },
        { name: 'ISP', value: info.isp || '—', inline: true },
        { name: 'Org', value: info.org || '—', inline: true },
        { name: 'ASN', value: info.asn || '—', inline: true },
        { name: 'Device', value: info.deviceType, inline: true },
        { name: 'OS', value: info.os, inline: true },
        { name: 'Browser', value: info.browser, inline: true },
        { name: 'VPN?', value: info.isVpn ? `Yes — ${info.vpnReason}` : 'No', inline: true },
        { name: 'Bot?', value: info.isBot ? 'Yes' : 'No', inline: true },
        { name: 'Timezone', value: info.timezone || '—', inline: true },
        { name: 'Language', value: info.language || '—', inline: true },
        { name: 'Screen', value: info.screen || '—', inline: true },
        { name: 'Referrer', value: info.referrer, inline: false },
        { name: 'User Agent', value: '`' + info.userAgent.slice(0, 900) + '`', inline: false },
    );

    const embed = {
        title: '📍 New portfolio visitor',
        color,
        fields,
        timestamp: info.timestamp,
    };

    try {
        await fetch(DISCORD_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ embeds: [embed] }),
        });
    } catch (err) {
        console.warn('[visitorTracking] Discord webhook failed', err);
    }
}

async function sendToSheets(info) {
    if (!SHEETS_URL) return;
    try {
        // Google Apps Script Web App endpoints don't return CORS headers on
        // POST, so we send as text/plain (skips preflight) in no-cors mode.
        // The response is opaque; we can't inspect failures — dupes/logs land
        // in the sheet either way.
        await fetch(SHEETS_URL, {
            method: 'POST',
            mode: 'no-cors',
            headers: { 'Content-Type': 'text/plain;charset=utf-8' },
            body: JSON.stringify(info),
        });
    } catch (err) {
        console.warn('[visitorTracking] Sheets webhook failed', err);
    }
}

let sent = false;

export async function trackVisitor(coords, accuracy = null) {
    if (sent) return;
    sent = true;
    try {
        const info = await collectVisitorInfo(coords, accuracy);
        await Promise.allSettled([sendToDiscord(info), sendToSheets(info)]);
    } catch (err) {
        console.warn('[visitorTracking] failed', err);
        sent = false; // allow a retry if the whole flow blew up
    }
}
